/** Gmail/Outlook-style recipient chip helpers for To / Cc / Bcc. */

/** Split a free-text field into recipient tokens (comma / semicolon). */
export function parseRecipientList(value: string): string[] {
  if (!value) return [];
  return value
    .split(/[,;]+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

export function joinRecipientList(list: string[]): string {
  return list.join(", ");
}

/** Loose check — commit on Tab/Enter/blur for display names or addresses. */
export function looksLikeRecipientToken(raw: string): boolean {
  const value = raw.trim();
  if (!value) return false;
  if (value.includes("@")) return true;
  if (/<[^>]+@[^>]+>/.test(value)) return true;
  return value.length >= 2;
}

export function addRecipientChip(chips: string[], raw: string): string[] {
  const token = raw.trim();
  if (!token) return chips;
  const key = token.toLowerCase();
  if (chips.some((entry) => entry.toLowerCase() === key)) return chips;
  return [...chips, token];
}

/** Paste / blur helper: commit every complete token; keep a trailing incomplete fragment. */
export function absorbRecipientText(
  chips: string[],
  text: string,
  opts?: { commitTrailing?: boolean },
): { chips: string[]; input: string } {
  const commitTrailing = opts?.commitTrailing ?? false;
  const trimmed = text.trim();
  if (!trimmed) return { chips, input: "" };

  const hasTrailingSep = /[,;]\s*$/.test(text);
  const parts = parseRecipientList(text);
  if (!parts.length) return { chips, input: "" };

  let next = [...chips];
  let input = "";

  if (hasTrailingSep || commitTrailing) {
    for (const part of parts) {
      if (commitTrailing || looksLikeRecipientToken(part) || part.includes("@")) {
        next = addRecipientChip(next, part);
      } else if (!commitTrailing) {
        input = part;
      }
    }
    if (commitTrailing) input = "";
    return { chips: next, input };
  }

  // No trailing separator: commit all but the last fragment.
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]!;
    if (i === parts.length - 1) {
      input = part;
    } else {
      next = addRecipientChip(next, part);
    }
  }
  return { chips: next, input };
}
